import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { Icon } from "@iconify/react";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import { Button, IconButton, MenuItem, Modal, TextField } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import "./Components.css";
import "./ResultCallModal.css";
import { stateAction } from "../Store/Actions/AuthAction";
import { saveACDInteraction } from "../Store/Actions/CallAction";
import { saveAssignmentWithSchedule } from "../Store/Actions/OutboundAction";
import { clearSessionCall, endSession } from "../Store/SIP/call";

import {
  GET_NEXT_ASSIGNMENT,
  SET_AUTO_DIAL,
  SET_DIAL_MODE,
  SET_PENDING_STATUS_REASON,
} from "../Store/Types/AgentTypes";

import { RESET_CALL_STATE, SET_GO_TO_ACW } from "../Store/Types/CallTypes";

const ResultCallModal = ({
  inboundContactID,
  isOpen,
  ISPlan,
  setIsResultCallModalShown,
  isFromChat,
  confirmEndChat,
  chatSource,
  handleEndChatWABA,
}) => {
  const [ISGroupID, setISGroupID] = useState("");
  const [ISCodeID, setISCodeID] = useState("");
  const [actionCodeReason, setActionCodeReason] = useState("");
  const [actionCode, setActionCode] = useState("");
  const [actionCodeID, setActionCodeID] = useState(null);
  const [statusGroupOptions, setStatusGroupOptions] = useState(null);
  const [statusOptions, setStatusOptions] = useState(null);
  const [nextActionCode, setNextActionCode] = useState(""); // For save result progressive call
  const [followUp, setFollowUp] = useState(false);
  const [followUpSchedule, setFollowUpSchedule] = useState(moment());
  const [notes, setNotes] = useState("");
  const [customData, setCustomData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [product, setProduct] = useState("");
  const dispatch = useDispatch();
  const { myInfo } = useSelector((state) => state.auth);

  const {
    callTime,
    campaignID,
    contactID,
    isInACW,
    phoneNumber,
    phoneTypeID,
    stateCall,
    type,
    isTelemarketing,
  } = useSelector((state) => state.call);

  const { pendingStatusReason, dialMode } = useSelector((state) => state.agent);
  const { currentAssignmentID } = useSelector((state) => state.outbound);

  useEffect(() => {
    // Get unique status group options
    const uniqueStatusGroup = [
      ...new Map(ISPlan[0]?.map((item) => [item["ISGroupID"], item])).values(),
    ];

    setStatusGroupOptions(
      uniqueStatusGroup.map((item) => {
        return {
          ISGroupID: item.ISGroupID ? item.ISGroupID : 0,
          statusGroup: item.StatusGroup ? item.StatusGroup : "-",
        };
      })
    );
  }, [ISPlan]);

  const renderCustomFields = () => {
    const sortedCustomFields = ISPlan[1]?.sort(
      (a, b) => a.Sequence - b.Sequence
    );

    let customFields = [];

    sortedCustomFields?.forEach((field) => {
      let conditionalSplit;
      let conditionalType;
      let conditionalCodes;

      if (field.ConditionalList) {
        // Split the conditional list to get type and codes
        conditionalSplit = field.ConditionalList.split("=");
        conditionalType = conditionalSplit[0];
        conditionalCodes = conditionalSplit[1].split("#");
      }

      // Check if the conditions are met for fields that have conditional list value
      const isConditionMatch = () => {
        if (conditionalType === "ISCode") {
          return conditionalCodes?.some((code) => Number(code) === ISCodeID);
        } else {
          return conditionalCodes?.some((code) => Number(code) === ISGroupID);
        }
      };

      // Render the custom field if it doesn't have a conditional list value, or the conditions are met
      if (!field.ConditionalList || isConditionMatch()) {
        switch (field.TypeData) {
          case "DATE":
            customFields.push(
              <div className="item" key={field.CustomID}>
                <DateTimePicker
                  ampm={false}
                  label={field.LabelName}
                  name
                  renderInput={(props) => <TextField {...props} fullWidth />}
                  slotProps={{
                    textField: {
                      id: field.FieldName,
                      name: field.FieldName,
                    },
                  }}
                  value={customData[`${field.FieldName}|${field.Sequence}`]}
                  onChange={(newValue) => {
                    let tempCustomData = customData;

                    tempCustomData[`${field.FieldName}|${field.Sequence}`] =
                      newValue;

                    setCustomData(tempCustomData);
                  }}
                />
              </div>
            );

            break;

          case "DROPDOWN":
            customFields.push(
              <div className="item" key={field.CustomID}>
                <TextField
                  fullWidth
                  id={field.FieldName}
                  label={field.LabelName}
                  name={field.FieldName}
                  select
                  value={customData[`${field.FieldName}|${field.Sequence}`]}
                  onChange={(e) => {
                    let tempCustomData = customData;

                    tempCustomData[`${field.FieldName}|${field.Sequence}`] =
                      e.target.value;

                    setCustomData(tempCustomData);
                  }}
                >
                  {field.ValueList.split("#").map((value) => (
                    <MenuItem value={value}>{value}</MenuItem>
                  ))}
                </TextField>
              </div>
            );

            break;

          case "TEXTBOX":
            customFields.push(
              <div className="item" key={field.CustomID}>
                <TextField
                  fullWidth
                  id={field.FieldName}
                  label={field.LabelName}
                  name={field.FieldName}
                  value={customData[`${field.FieldName}|${field.Sequence}`]}
                  onChange={(e) => {
                    let tempCustomData = customData;

                    tempCustomData[`${field.FieldName}|${field.Sequence}`] =
                      e.target.value;

                    setCustomData(tempCustomData);
                  }}
                />
              </div>
            );

            break;

          default:
            break;
        }
      }
    });

    return customFields;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (isFromChat) {
      if (chatSource === "WA") {
        confirmEndChat(2, null, null, actionCode, actionCodeReason, notes);
        setIsSubmitting(false);
      } else {
        handleEndChatWABA({
          ISCode: actionCode,
          ISCodeReason: actionCodeReason,
          remarks: notes,
          type: 2,
        });

        setIsSubmitting(false);
      }
    } else if (type === "inbound" || type === "INBOUND") {
      // Commented keys might be used later
      const payload = {
        acddeviceid: stateCall.ACDDeviceID,
        actioncode: actionCode,
        actioncodereason: actionCodeReason,
        callsource: "INBOUND",
        calltime: moment(stateCall.CallTime).toISOString(true),
        contactid: inboundContactID,

        customdatajson:
          Object.keys(customData).length > 0
            ? JSON.stringify(customData)
            : null, // Custom data

        disconnecttime: moment(stateCall.receivedDate).toISOString(true),

        followupdate: followUp
          ? followUpSchedule.format("YYYY-MM-DDTHH:mm:ss.SSSZ")
          : null,

        holdduration: 0,
        phoneno: phoneNumber,
        receiveddate: moment(stateCall.receivedDate).toISOString(true),
        recordingfile: stateCall.RecFileName,
        remarks: notes,
        talkduration: moment().diff(callTime, "seconds"),
        uniqueid: stateCall.UniqueID ? stateCall.UniqueID : 0,
      };

      saveACDInteraction(payload)
        .then((response) => {
          if (response.status) {
            console.log("response", response.data);
            dispatch({ type: SET_GO_TO_ACW, payload: false });
            setIsSubmitting(false);

            toast.success("Saved successfully", {
              autoClose: 3000,
            });

            setISCodeID("");
            setISGroupID("");
            setIsResultCallModalShown(false);
            dispatch(clearSessionCall());
            dispatch({ type: RESET_CALL_STATE });
            dispatch(stateAction("READY", undefined, "ACW", "Green"));
          } else {
            console.log("error", response.error);
            toast.error("Error saving result.", { autoClose: 3000 });
            setIsSubmitting(false);
          }
        })
        .catch((err) => {
          console.log("err", err);
          toast.error("Error saving result.", { autoClose: 3000 });
          setIsSubmitting(false);
        });
    } else {
      let payload;

      if (isTelemarketing) {
        payload = {
          actioncode: actionCode,
          actioncodeid: actionCodeID,
          actioncodereason: actionCodeReason,
          agenttssid: myInfo.user.AgentTSS.AgentID,
          assid: currentAssignmentID,
          callsource: stateCall.CallType ?? "OUTBOUND",
          calltime: callTime.toISOString(),
          campaignid: campaignID,
          contactid: contactID,
          customdata: null,
          customsaveinput: null,
          duration: moment().diff(callTime, "seconds"),
          followupdate: followUp ? followUpSchedule.toISOString() : null,
          iswabacall: stateCall.IsWACall ?? false,
          nextaction: nextActionCode,
          phoneno: phoneNumber,
          phonetype: phoneTypeID,
          product: product,
          recordingfile: stateCall.RecFileName,
          remarks: notes,
          source: "TSS",
          trycount: 1,
          uniqueid: stateCall.UniqueID ? stateCall.UniqueID : 0,
        };
      } else {
        // Commented keys might be used later
        payload = {
          agentid: myInfo.user.AgentID,
          // ActionCallID: "52077944_4", // Action ID (state call)
          // Channel: "PJSIP/quirosdpi-0000040f", // (state call)
          // "UniqueCallID": "1693367271.5975", // Unique ID? (state call)
          phoneno: phoneNumber,
          phonetype: phoneTypeID, // PhoneTypeID?
          // "PhoneTypeName": "Mobile",
          uniqueid: stateCall.UniqueID ? stateCall.UniqueID : 0, // Unique ID? (state call)
          campaignid: campaignID,
          // "CustomerName": "Joshua Dylan",
          // "CampaignName": "COLLECTION",
          contactid: contactID,
          // "CallTypes": "MANUALCALL", // MANUALCALL || INBOUND || AUTOMATICCALL || WHATSAPP
          callsource: stateCall.CallType ?? "OUTBOUND", // WHATSAPP || AUTOMATICCALL || MANUALCALL
          calltime: callTime.toISOString(true), // Tanggal telepon
          // "ActionGroup": "BERHASIL DIHUBUNGI",
          // "ActionCodeID": 39,
          // "ActionCodeReasonID": 1,
          actioncodereason: actionCodeReason,
          actioncode: actionCode,
          nextaction: nextActionCode, //
          trycount: 1, // default 1;
          // "ActionState": null, // IS
          remarks: notes, // Notes
          followupdate: followUp ? followUpSchedule.toISOString(true) : null,
          duration: moment().diff(callTime, "seconds"), // dari call time sampai save, seconds
          // "TalkDuration": 11000, // dari up sampai putus
          // "AnswerTime": "2023-08-30T00:00:00Z",
          // "HoldTime": 0, // kalau kosong 0
          // "HoldDuration": 0, // kalau kosong 0
          // "DisconnectTime": "2023-08-30T00:00:00Z",
          // "DisconnectByUser": true, // tanya kevin
          recordingfile: stateCall.RecFileName,
          // "AgentCode": "tm_test2", // username
          // "AgentTSSID": null, // untuk TSS
          // "AgentExt": "PJSIP/1024",
          // "ACDActionData": null, // kosongin dulu
          // "CustomData": null, // kosongin dulu
          // "CallIndexAutoDial": null, // kosongin dulu
          // "ParkingSpace": null, // transfer
          // "ActionCallHoldID": null, // transfer
          // "IsTransfer": false, // transfer
          // "EnabledHD": null, // // kosongin dulu
          // "IsCancelCall": false,
          // "Context": "dpi",
          assid: currentAssignmentID,
          // "Action": null, // Tanya kevin

          customdatajson:
            Object.keys(customData).length > 0
              ? JSON.stringify(customData)
              : null, // Custom data

          wa_id: null, // kalo dari WA
          iswabacall: stateCall.IsWACall ?? false,
        };
      }

      saveAssignmentWithSchedule(payload)
        .then((response) => {
          if (response.status) {
            console.log("Output interaction ID", response.data.interactionid);
            setIsSubmitting(false);

            toast.success("Saved successfully", {
              autoClose: 3000,

              onClose: () => {
                if (!response.data.issubmitapp) {
                  if (
                    dialMode === "progressive" &&
                    pendingStatusReason === ""
                  ) {
                    dispatch({ type: GET_NEXT_ASSIGNMENT });
                  } else if (
                    dialMode === "predictive" &&
                    pendingStatusReason === ""
                  ) {
                    dispatch({ type: SET_AUTO_DIAL, payload: false });

                    dispatch({
                      type: SET_DIAL_MODE,
                      payload: "",
                    });
                  }
                }
              },
            });

            setISCodeID("");
            setISGroupID("");
            setIsResultCallModalShown(false);
            dispatch({ type: SET_GO_TO_ACW, payload: false });
            // dispatch(clearSessionCall("logout"));
            dispatch(endSession());

            if (response.data.issubmitapp) {
              toast.warn("Please fill application for the next action!", {
                autoClose: false,
              });
            } else {
              if (pendingStatusReason !== "") {
                // If there is a pending status change, set status to the pending status
                dispatch(
                  stateAction("AUX", pendingStatusReason, "ACW", "Orange")
                );

                dispatch({ type: SET_PENDING_STATUS_REASON, payload: "" });
                dispatch({ type: SET_AUTO_DIAL, payload: false });
              } else {
                // Else, change status to READY
                if (isInACW) {
                  dispatch({ type: RESET_CALL_STATE });
                  dispatch(stateAction("READY", undefined, "ACW", "Green"));
                }
              }
            }
          } else {
            console.log("error", response.error);

            toast.error("Error saving result: " + response?.error, {
              autoClose: 3000,
            });

            setIsSubmitting(false);
          }
        })
        .catch((err) => {
          console.log("err", err);
          toast.error("Error saving result: " + err, { autoClose: 3000 });
          setIsSubmitting(false);
        });
    }
  };

  return (
    <Modal
      open={isOpen}
      className="modal result-call"
      onClose={() => setIsResultCallModalShown(false)}
    >
      <div className="modal-container">
        <div className="modal-content">
          <IconButton
            className="close-button"
            onClick={() => setIsResultCallModalShown(false)}
          >
            <CloseOutlined />
          </IconButton>

          <h2 className="title">
            {isFromChat ? "Add Interaction" : "Result Call"}
          </h2>

          <form method="post" onSubmit={handleSubmit}>
            <section className="form-inputs">
              {isFromChat && (
                <h4 style={{ textAlign: "center" }}>
                  Caution! You are about to end this chat. Please fill this
                  interaction form.
                </h4>
              )}

              <div className="item">
                <TextField
                  fullWidth
                  id="statusGroup"
                  label="Status Group"
                  name="statusGroup"
                  required
                  select
                  value={ISGroupID}
                  onChange={(e) => {
                    setISGroupID(e.target.value);
                    setISCodeID("");

                    setStatusOptions(
                      ISPlan[0]
                        .filter((item) =>
                          e.target.value
                            ? item.ISGroupID === e.target.value
                            : !item.ISGroupID
                        )
                        .map((item) => {
                          return {
                            ISCode: item.ISCode,
                            ISCodeID: item.ISCodeID,
                            status: item.Status,
                            nextActionCode: item.NextActionCode,
                          };
                        })
                    );
                  }}
                >
                  {statusGroupOptions?.map((option) => (
                    <MenuItem
                      key={option.ISGroupID}
                      value={option.ISGroupID}
                      onClick={() => setActionCodeReason(option.statusGroup)}
                    >
                      {option.statusGroup}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              <div className="item">
                <TextField
                  fullWidth
                  id="status"
                  label="Status"
                  name="status"
                  required
                  select
                  value={ISCodeID}
                  onChange={(e) => setISCodeID(e.target.value)}
                >
                  {statusOptions ? (
                    statusOptions.map((option) => {
                      return (
                        <MenuItem
                          key={option.ISCodeID}
                          value={option.ISCodeID}
                          onClick={() => {
                            setActionCode(option.ISCode);
                            setNextActionCode(option.nextActionCode);
                            setActionCodeID(option.ISCodeID);
                          }}
                        >
                          {option.status}
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem value="0" disabled>
                      Select status group
                    </MenuItem>
                  )}
                </TextField>
              </div>

              {isTelemarketing && (
                <>
                  <div className="item">
                    <TextField
                      fullWidth
                      id="productOffer"
                      label="Product Offer"
                      name="productOffer"
                      required
                      select
                      value={product}
                      onChange={(e) => {
                        setProduct(e.target.value);
                      }}
                    >
                      {ISPlan[2]?.map((product) => (
                        <MenuItem
                          key={product.id}
                          value={`${product.id}##${product.name}`}
                        >
                          {product.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>

                  {/* Temp hidden */}
                  {/* <div className="item">
                    <TextField
                      fullWidth
                      id="loanID"
                      label="Loan ID"
                      name="loanID"
                    />
                  </div> */}
                </>
              )}

              <div className="item">
                <TextField
                  fullWidth
                  id="notes"
                  label="Notes"
                  multiline
                  name="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              <div className="follow-up">
                <hr />

                <Button
                  className={followUp ? "active" : ""}
                  variant="outlined"
                  startIcon={
                    <Icon
                      icon={
                        followUp
                          ? "mingcute:schedule-fill"
                          : "material-symbols:date-range-rounded"
                      }
                    />
                  }
                  onClick={() => setFollowUp((prevState) => !prevState)}
                >
                  Schedule a Follow up
                </Button>

                <hr />
              </div>

              {followUp && (
                <div className="item">
                  <DateTimePicker
                    ampm={false}
                    label="Follow Up Schedule"
                    name
                    renderInput={(props) => <TextField {...props} fullWidth />}
                    slotProps={{
                      textField: {
                        id: "ReportDate",
                        name: "ReportDate",
                      },
                    }}
                    value={followUpSchedule}
                    onChange={(newValue) => {
                      setFollowUpSchedule(newValue);
                    }}
                  />
                </div>
              )}

              {renderCustomFields(ISCodeID)}
            </section>

            <section className="buttons">
              <button
                className="cancel"
                type="button"
                onClick={() => setIsResultCallModalShown(false)}
              >
                Cancel
              </button>

              <button className="confirm" disabled={isSubmitting} type="submit">
                {isSubmitting && <Icon height="22" icon="eos-icons:loading" />}
                {isFromChat ? "Save & End Chat" : "Save & Disconnect"}
              </button>
            </section>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default ResultCallModal;
